import { INSTRUMENT_DEFINITIONS } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";

// health_questionnaire's own question count, read from the same definitions
// the questionnaire engine answers against (useQuestionnaire.ts) rather than
// a second hardcoded number — repeating-group items aren't in this count, so
// neither is a client's answers to them (steps.ts's readProgress matches).
const SCREENER_TOTAL_QUESTIONS = INSTRUMENT_DEFINITIONS.health_questionnaire.questions.filter((q) => !q.retired).length;

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

interface ScreenerProgress {
  state: string;
  answeredCount?: number;
  lastSavedAt?: string | null;
}

/* Where an in-progress screener got to, for any steps card that shows it:
 * "Vastasit 18 kysymykseen 26:sta. Viimeksi eilen klo 21.40." Undefined
 * until there is something to say — the submission is open but nothing has
 * been saved yet — so the caller falls back to its generic sentence.
 *
 * The keys keep their infusion.* names: the infusion tab had this first, and
 * renaming them would only churn the translations. */
export function useScreenerResumeLine(): (step: ScreenerProgress) => string | undefined {
  const { t } = useTranslation("client");
  const formatters = useFormatters();

  // "tänään klo 21.40" / "eilen klo 21.40" / "3.9. klo 21.40" — the same three
  // buckets a chat timestamp would use, but there's no shared helper for it
  // yet (see SampleAtLabBlock/ResultsScreen for the two other one-off
  // Intl.DateTimeFormat call sites this mirrors).
  function formatLastSaved(lastSavedAt: string): string {
    const saved = new Date(lastSavedAt);
    const now = new Date();
    const time = formatters.time.format(saved);
    if (isSameDay(saved, now)) {
      return t("infusion.steps.screener.lastSavedToday", "tänään klo {{time}}", { time });
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (isSameDay(saved, yesterday)) {
      return t("infusion.steps.screener.lastSavedYesterday", "eilen klo {{time}}", { time });
    }
    return t("infusion.steps.screener.lastSavedOn", "{{date}} klo {{time}}", {
      date: formatters.date.format(saved),
      time,
    });
  }

  return (step) =>
    step.state === "in_progress" && step.answeredCount !== undefined && step.lastSavedAt
      ? t("infusion.steps.screener.resume", "Vastasit {{answered}} kysymykseen {{total}}:sta. Viimeksi {{lastSaved}}.", {
          answered: step.answeredCount,
          total: SCREENER_TOTAL_QUESTIONS,
          lastSaved: formatLastSaved(step.lastSavedAt),
        })
      : undefined;
}
